import React from 'react';
import Divider from '~/components/atom/Divider';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Label } from '~/components/atom/Typography';
import CopyTextBlock from '~/components/molecule/CopyTextBlock';
import type { Address } from '~/graphql/types';
import useOffices from '~/hooks/useOffices';
import formatAddressAsString from '~/LFA/components/pages/Contact/utils/formatAddressAsString';
import type { ClientLfaConversion } from '~/LFA/graphql/client';

const text = {
  location: 'Locatie van de afspraak',
  byPhone: 'via telefoon',
  byOffice: 'op kantoor',
  onLocation: 'op locatie',
};

export type Props = {
  dataTestId?: string;
  className?: string;
  location: ClientLfaConversion['location'];
};

const MeetingLocation: React.FCC<Props> = ({
  dataTestId,
  className,
  location,
}) => {
  const offices = useOffices({ onlyExistingOffices: true });

  const locationToDisplay = (() => {
    switch (location?.__typename) {
      case 'LFA_Conversion_Location_Office': {
        const officeOfTheMeeting = offices.find(
          o => o.id === location.officeId,
        );
        if (officeOfTheMeeting) {
          return {
            label: text.byOffice,
            value: `${officeOfTheMeeting.name} - ${formatAddressAsString(officeOfTheMeeting.address as Address)}`,
          };
        }

        return {
          label: text.byOffice,
          value: location.officeId,
        };
      }
      case 'LFA_Conversion_Location_Phone':
        return {
          label: text.byPhone,
          value: location.phone,
        };
      case 'LFA_Conversion_Location_OnLocation':
        return {
          label: text.onLocation,
          value: formatAddressAsString(location.address as Address),
        };

      default:
        return null;
    }
  })();

  if (!locationToDisplay) return null;

  return (
    <JustificationContainer
      dataTestId={dataTestId}
      className={className}
      gap="base"
      width="100%"
      direction="column"
    >
      <Divider margin={[null]} />
      <JustificationContainer align="center" gap="s">
        <Label size="base" margin={[null]}>
          {text.location} ({locationToDisplay.label}):
        </Label>
        <CopyTextBlock textToCopy={locationToDisplay.value} />
      </JustificationContainer>
      <Divider margin={[null]} />
    </JustificationContainer>
  );
};

export default MeetingLocation;
